"use strict";
/**
 * Vector Fields and Flow Lines calculations
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.scaleVector = exports.normalizeVector = exports.getDefaultFields = exports.getFieldDescription = exports.updateParticles = exports.generateVectorField = exports.calculateVectorField = void 0;
/**
 * Calculate vector field at a given point
 */
function calculateVectorField(x, y, config) {
    const dx = x - config.centerX;
    const dy = y - config.centerY;
    const r = Math.sqrt(dx * dx + dy * dy);
    const r2 = r * r;
    const r3 = r2 * r;
    switch (config.type) {
        case 'gravity':
            // F = -G * m / r² * r̂
            if (r < 0.1)
                return { x: 0, y: 0 }; // Avoid singularity
            const gravityMagnitude = config.strength * (config.mass || 1) / r2;
            return {
                x: -gravityMagnitude * dx / r,
                y: -gravityMagnitude * dy / r
            };
        case 'electromagnetic':
            // E = k * q / r² * r̂
            if (r < 0.1)
                return { x: 0, y: 0 };
            const emMagnitude = config.strength * (config.charge || 1) / r2;
            return {
                x: emMagnitude * dx / r,
                y: emMagnitude * dy / r
            };
        case 'gradient':
            // Gradient of f(x,y) = x² + y²
            return {
                x: 2 * x * config.strength,
                y: 2 * y * config.strength
            };
        case 'vortex':
            // Circular flow: v = ω × r
            const vortexMagnitude = config.strength * (config.frequency || 1);
            return {
                x: -vortexMagnitude * dy,
                y: vortexMagnitude * dx
            };
        case 'sink':
            // Radial inward flow
            if (r < 0.1)
                return { x: 0, y: 0 };
            const sinkMagnitude = -config.strength / r;
            return {
                x: sinkMagnitude * dx / r,
                y: sinkMagnitude * dy / r
            };
        case 'source':
            // Radial outward flow
            if (r < 0.1)
                return { x: 0, y: 0 };
            const sourceMagnitude = config.strength / r;
            return {
                x: sourceMagnitude * dx / r,
                y: sourceMagnitude * dy / r
            };
        default:
            return { x: 0, y: 0 };
    }
}
exports.calculateVectorField = calculateVectorField;
/**
 * Generate vector field data for visualization
 */
function generateVectorField(width, height, config, gridSize = 20) {
    const vectors = [];
    // Generate grid of vectors
    for (let x = gridSize; x < width - gridSize; x += gridSize) {
        for (let y = gridSize; y < height - gridSize; y += gridSize) {
            const vector = calculateVectorField(x, y, config);
            const magnitude = Math.sqrt(vector.x * vector.x + vector.y * vector.y);
            vectors.push({
                x,
                y,
                vx: vector.x,
                vy: vector.y,
                magnitude
            });
        }
    }
    // Generate initial particles
    const particles = [];
    const numParticles = 20;
    for (let i = 0; i < numParticles; i++) {
        const angle = (i / numParticles) * 2 * Math.PI;
        const radius = Math.min(width, height) * 0.3;
        const x = config.centerX + radius * Math.cos(angle);
        const y = config.centerY + radius * Math.sin(angle);
        // Give particles a small initial velocity to make them visible
        const initialSpeed = 0.5;
        const initialAngle = angle + Math.PI / 2; // Perpendicular to radius
        particles.push({
            x,
            y,
            vx: initialSpeed * Math.cos(initialAngle),
            vy: initialSpeed * Math.sin(initialAngle),
            trail: [],
            maxTrailLength: 50
        });
    }
    return { vectors, particles };
}
exports.generateVectorField = generateVectorField;
/**
 * Update particle positions using Runge-Kutta integration
 */
function updateParticles(particles, config, dt = 0.1) {
    return particles.map(particle => {
        // Get current velocity from vector field
        const field = calculateVectorField(particle.x, particle.y, config);
        // Update velocity (with some damping)
        const damping = 0.99; // Reduced damping for longer trails
        const newVx = (particle.vx + field.x * dt * 0.1) * damping;
        const newVy = (particle.vy + field.y * dt * 0.1) * damping;
        // Update position
        const newX = particle.x + newVx * dt;
        const newY = particle.y + newVy * dt;
        // Add to trail
        const newTrail = [...particle.trail, { x: particle.x, y: particle.y }];
        if (newTrail.length > particle.maxTrailLength) {
            newTrail.shift();
        }
        return {
            x: newX,
            y: newY,
            vx: newVx,
            vy: newVy,
            trail: newTrail,
            maxTrailLength: particle.maxTrailLength
        };
    });
}
exports.updateParticles = updateParticles;
/**
 * Get field description for UI
 */
function getFieldDescription(config) {
    switch (config.type) {
        case 'gravity':
            return `Gravitational field around a mass of ${config.mass || 1} units. Force points toward the center.`;
        case 'electromagnetic':
            return `Electric field around a charge of ${config.charge || 1} units. Field lines radiate outward from positive charge.`;
        case 'gradient':
            return `Gradient field of f(x,y) = x² + y². Points in direction of steepest increase.`;
        case 'vortex':
            return `Vortex field with angular frequency ${config.frequency || 1}. Particles follow circular paths.`;
        case 'sink':
            return `Sink field. Particles flow inward toward the center.`;
        case 'source':
            return `Source field. Particles flow outward from the center.`;
        default:
            return 'Unknown field type.';
    }
}
exports.getFieldDescription = getFieldDescription;
/**
 * Get default field configurations
 */
function getDefaultFields() {
    return {
        'Gravitational Field': {
            type: 'gravity',
            strength: 100,
            centerX: 400,
            centerY: 300,
            mass: 1
        },
        'Electric Field': {
            type: 'electromagnetic',
            strength: 100,
            centerX: 400,
            centerY: 300,
            charge: 1
        },
        'Gradient Field': {
            type: 'gradient',
            strength: 0.1,
            centerX: 400,
            centerY: 300
        },
        'Vortex Field': {
            type: 'vortex',
            strength: 0.5,
            centerX: 400,
            centerY: 300,
            frequency: 1
        },
        'Sink Field': {
            type: 'sink',
            strength: 50,
            centerX: 400,
            centerY: 300
        },
        'Source Field': {
            type: 'source',
            strength: 50,
            centerX: 400,
            centerY: 300
        }
    };
}
exports.getDefaultFields = getDefaultFields;
/**
 * Normalize vector to unit length
 */
function normalizeVector(v) {
    const magnitude = Math.sqrt(v.x * v.x + v.y * v.y);
    if (magnitude === 0)
        return { x: 0, y: 0 };
    return {
        x: v.x / magnitude,
        y: v.y / magnitude
    };
}
exports.normalizeVector = normalizeVector;
/**
 * Scale vector by a factor
 */
function scaleVector(v, scale) {
    return {
        x: v.x * scale,
        y: v.y * scale
    };
}
exports.scaleVector = scaleVector;
